import { useEffect, useRef, useState } from 'react'
import { FISK_PATH, FISK_TRANSFORM, FISK_VIEWBOX } from '../fisk'
import {
  ApiError, siteLayout,
  type FlowHour, type Occupancy, type PenCount, type SiteLayout,
} from '../api'
import {
  chevronCount, flowArrows, offset, penAt, penFeature, radiusM, slackRing,
  structureFeature, type DrawnFeature,
} from '../merdgeo'
import { startStroemning, type Stroemning } from '../stroemning'
import { startSverm, type Sverm } from '../sverm'
import { kompass } from './Stroemstripe'
import { antallFor, nb } from './Varsel'
import { Link } from '../router'
import { useT } from '../lang'

/* The farm, seen from above, with what was counted in each ring.
 *
 * WHY THIS IS THE MAIN SCREEN AND THE TABLE IS NOT.
 *
 * "Merd 3 is always the worst" is a sentence about the mouth of the fjord and
 * the way the current sets past it. A table sorted by merd number cannot say
 * it; eight polygons on a satellite picture say it without anybody writing an
 * analysis. The two rings at the outer end run warm every spring and the two in
 * the lee do not, and that is a fact about geography that only shows up as
 * geography.
 *
 * It is also the answer to the §9 question in a form somebody can act on.
 * "Treat all production units unless it can be documented that treating all of
 * them is obviously unnecessary" is a claim about which rings, and a picture of
 * which rings is the documentation.
 *
 * THE THREE COLOURS ARE THE FORECAST'S OWN BANDS, NOT NEW ONES.
 *
 * `antallFor` is the rule the model publishes on /v2/symboler — under half the
 * limit, half to whole, at or over — and it is imported rather than restated so
 * that a ring on this map and a week in the strip below it cannot end up on two
 * different ladders. The tokens are mapped by ROLE and not by hue, exactly as
 * styles.css does for the strip: --signal low, --warn medium, --alert-text over.
 *
 * A RING WITH NO COUNT IS NOT DRAWN GREEN. It is drawn hollow and grey, because
 * "nobody has looked" and "we looked and it was fine" are different sentences
 * and a picture that renders them the same colour is a picture that lies once a
 * week. Vedlegg 1 asks for every ring; the ones missing are the point.
 *
 * WHAT THE COLOUR MEANS: adult female lice per fish, at that ring, on the most
 * recent day the farm counted — and nothing else. Not mobiles, not sessiles.
 * The limit in lakselusforskriften is written against adult females alone, it
 * is the number that decides whether the farm must treat, and it is the number
 * sent to Mattilsynet. A map that mixed stages would be colouring by a quantity
 * no rule is expressed in.
 *
 * WHEN NOBODY HAS COUNTED PER RING, EVERY RING SHOWS THE FARM'S OWN FIGURE.
 *
 * The alternative — eight hollow rings on a farm that has a perfectly good
 * count — makes a working product look empty, and the number IS true of the
 * farm. So it is painted across all of them, one colour, exactly as asked.
 *
 * But it is drawn as what it is: a DASHED outline and a caption saying so. It
 * has to be, because the failure it invites is specific and dangerous. A farm
 * at 0,45 is under the 0,50 limit and would paint eight amber rings — while the
 * reality behind that average might be one ring at 0,9 and seven at 0,38, and
 * the picture would be saying "no ring is over" at the exact moment one is.
 * Same colour, and never the claim that we measured it there.
 *
 * ALL OR NOTHING. If even one ring was counted that day, the ones that were not
 * stay hollow: there IS individual data, and a blank ring is then a real gap
 * that vedlegg 1 cares about rather than an absence of the whole layer.
 *
 * ONCE THE FISH ARE MODELLED, THE COLOUR FOLLOWS THEM AND NOT THE RING.
 *
 * A farm that has said which population stands where gets a different and
 * better answer: each ring is coloured by the newest count OF THE FISH IN IT,
 * wherever that count was taken. Move merd 6's fish into merd 3 and merd 6 has
 * no fish and therefore no lice — colouring it with last week's figure would
 * state that an empty cage is over the limit, while merd 3 would go on showing
 * a figure belonging to fish that left. Both readings look completely normal
 * and would have somebody treating the wrong cage.
 *
 * An empty ring is drawn EMPTY, not as "not counted". Those are different
 * sentences: nobody looked, versus there is nothing to look at.
 *
 * THE RING IS THE PLACE AND THE BADGE IS THE FISH.
 *
 * A ring that holds a population carries a second, smaller marker beside its
 * number, and THAT is what you drag. In the staff editor you drag rings,
 * because geometry is set up once; here you drag fish, because moving them
 * between merder is an ordinary Tuesday — and the ring never moves. Two objects
 * on one map, and the difference between them is exactly the domain
 * distinction that matters. Nobody has to be told: you drag the thing that
 * moves.
 *
 * A separate marker rather than a third child of `.merdvis`, which is a
 * two-row grid (`<b>` over `<i>`) and would need an explicit grid-template to
 * take one. Separate also means the badge can be draggable while the ring
 * underneath is not, which is the whole point.
 */

const TOKEN = import.meta.env.VITE_MAPBOX_TOKEN as string | undefined

/* Mirrors the ladder in styles.css. Hardcoded because MapLibre paint runs in
   WebGL and cannot read a CSS variable; named after the token it copies. */
const BANDS: Record<number, string> = {
  1: '#b4d9c7', // --signal      · under half the limit
  2: '#e9c46a', // --warn        · half to whole
  3: '#ffb0a6', // --alert-text  · at or over
}
const C_NONE = '#93ab9f'      // --faint · not counted
const C_STRUCTURE = '#e6f1ea' // --text
/* THE CURRENT, BANDED BY HOW MUCH NEW WATER THE CAGE IS GETTING.
   The question this layer exists to answer is not "which way" — it is "is my
   cage being flushed right now". So the trail is coloured by that, and the
   ladder runs the same way the lice ladder does: pale green is fine, amber is
   worth a look. Reusing those two tokens is deliberate rather than lazy —
   green means good and amber means caution on both scales, so a farmer reads
   one convention and not two.

   `strong` is amber and not a brighter green, because this is NOT a
   more-is-better scale: past about 0.35 m/s the net deforms and the fish swim
   to hold station. Never --pulse — red means a detection event, an
   attribution conflict, or the hottest spectrogram bins, and a weather layer
   is none of the three. */
const STROEM_BAND: Record<string, string> = {
  slack: '#93ab9f',    // --faint    · barely moving
  weak: '#93ab9f',     // --faint    · too slow to renew a cage
  moderate: '#b9cec3', // --muted    · some exchange
  good: '#b4d9c7',     // --signal   · comfortable exchange
  strong: '#e9c46a',   // --warn     · the fish are working to hold station
}
const C_STROEM = '#b9cec3'    // --muted · the fallback

/** The ring's upper-left, in lon/lat.
 *
 *  A lon/lat and not a pixel offset, because a pixel offset is a fixed
 *  distance and a ring is not: at one zoom the mark would sit on the rim and
 *  at the next it would be adrift in the water. Placed geographically it stays
 *  on the same part of the same ring at every zoom, which is what makes it
 *  read as belonging to that ring rather than floating near it. */
function nordvest(lat: number, lon: number, r: number): [number, number] {
  return offset(lat, lon, -r * 0.72, r * 0.72)
}

/** How long the fish take to get there. Long enough to follow with the eye,
 *  short enough that a four-step shuffle is not four waits. */
const SVØMMETID = 520

/** A badge travelling from one ring to another.
 *
 *  Straight line, ease-out, and no arc: an arc would be drawing a path
 *  through water they did not take. The camera is not touched — moving the
 *  view while somebody is mid-shuffle is the one thing worse than a teleport.
 *
 *  `prefers-reduced-motion` skips it and the badge is simply there, which is
 *  the behaviour this had before and is never wrong. */
function svøm(mk: any, fra: [number, number], til: [number, number]): void {
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return
  const t0 = performance.now()
  const step = (now: number) => {
    const k = Math.min(1, (now - t0) / SVØMMETID)
    // ease-out cubic. Fast off the mark, settling into the ring — which is
    // how a net of fish actually arrives.
    const e = 1 - (1 - k) ** 3
    try {
      mk.setLngLat([fra[0] + (til[0] - fra[0]) * e,
                    fra[1] + (til[1] - fra[1]) * e])
    } catch { return }  // the marker was removed mid-swim
    if (k < 1) requestAnimationFrame(step)
  }
  requestAnimationFrame(step)
}

/** The badge's fish.
 *
 *  A real silhouette now rather than the two-path shape this drew before —
 *  a tail triangle and an ellipse, which read as a fish at 40px and as a
 *  lozenge at 14. See `fisk.ts` for where it comes from and why it is
 *  vendored.
 *
 *  No mouth and no eye. Those carry the welfare reading in `Fish.tsx`, and
 *  this marker says nothing about welfare — it says "there are fish here".
 */
function fishSvg(): SVGSVGElement {
  const ns = 'http://www.w3.org/2000/svg'
  const svg = document.createElementNS(ns, 'svg')
  svg.setAttribute('viewBox', FISK_VIEWBOX)
  /* 2.77:1, which is the silhouette's own proportion. Sized wider than the
     glyph it replaces and not taller: a fish is a long thing, and squaring it
     up to fill a badge is how a fish becomes a blob. */
  svg.setAttribute('width', '38')
  svg.setAttribute('height', '14')
  svg.setAttribute('aria-hidden', 'true')
  const g = document.createElementNS(ns, 'g')
  g.setAttribute('transform', FISK_TRANSFORM)
  g.setAttribute('fill', 'currentColor')
  const path = document.createElementNS(ns, 'path')
  path.setAttribute('d', FISK_PATH)
  g.append(path)
  svg.append(g)
  return svg
}

export function Anleggsvisning({
  siteId, counts, siteValue, grense, selected, onSelect, setupHref,
  occupancy, onMoveFish, onDropTarget, onArmFish, armedFrom, showNames,
  fyller, fullskjermHref, sisteFlytt, stroem,
}: {
  siteId: string
  /** One row per ring for the day being shown, including rings with nothing
   *  recorded — `pen_count_id: null` is the answer this map most needs. */
  counts: PenCount[]
  /** What the farm as a whole was reported at on this day. Used only when no
   *  ring has an individual count; see the header. */
  siteValue: number | null
  grense: number | null
  selected: string | null
  onSelect: (penId: string | null) => void
  /** Staff only: where to go and draw the farm. Absent for a customer, who
   *  cannot edit the ring set and should not be shown a door that says 403. */
  setupHref?: string
  /** Who is standing where on the day being shown. Absent means this farm has
   *  not modelled its fish, and then there are no badges and nothing changes —
   *  counting lice must never depend on somebody having done that first. */
  occupancy?: Occupancy[]
  /** Present only when the reader may record a move. A badge that can be
   *  dragged where the drag would 403 is worse than no badge. */
  onMoveFish?: (fromPenId: string, toPenId: string) => void
  /** Dropped somewhere that is not a ring — the slaughterhouse, the delousing.
   *  `kind` is whatever the tray element declared in `data-fishdrop`. */
  onDropTarget?: (fromPenId: string, kind: string) => void
  /** Tapped rather than dragged.
   *
   *  NOT A FALLBACK. Principle 2 of the design system: this is used on a
   *  phone, on a quay, in the rain, one handed — and "nothing depends on
   *  hover" has a sibling nobody wrote down, which is that nothing may depend
   *  on a drag either. A drag needs a press, a path and a release without
   *  losing contact; a tap needs one contact. In gloves, on a swell, only one
   *  of those is reliable. */
  onArmFish?: (penId: string) => void
  /** The ring a move has been armed from, drawn so the reader can see the
   *  gesture is half-finished. */
  armedFrom?: string | null
  /** More than one population at this farm. With one, every badge would read
   *  the same three letters — seven identical tags saying nothing, which is
   *  decoration by the design system's own definition. */
  showNames?: boolean
  /** Fill the parent instead of standing in a card at a fixed height.
   *
   *  The array at Ljøsøy is three hundred metres of diagonal, and a bounding
   *  box round a diagonal is mostly sea. At 460px that leaves the rings the
   *  size of a fingernail with the fjord doing nothing on both sides of them —
   *  which is the whole argument for the map having an address of its own. */
  fyller?: boolean
  /** Where the big version lives. Absent on the big version itself. */
  fullskjermHref?: string
  /** The move that has just been written, so the badge can SWIM there rather
   *  than being deleted from one ring and constructed in another.
   *
   *  The teleport is not a cosmetic problem. A shuffle is three or four moves
   *  in a row and the only confirmation the reader gets is the picture; a
   *  badge that vanishes and reappears makes them check that the right one
   *  went to the right place, every time. Watching it travel is the check. */
  sisteFlytt?: { occupancy_id: string; from_pen_id: string | null;
                 pen_id: string } | null
  /** One hour of the current forecast, already chosen by whoever owns the
   *  scrubber. Absent means this farm has not bought the forecast, or it could
   *  not be fetched, and then the map draws exactly as it always has — the
   *  currents are a layer on a picture that is mostly about lice, and they must
   *  never be able to take it with them.
   *
   *  An hour whose `current` is null is a gap in the forecast and draws
   *  nothing, which is different from an hour of slack water, which draws a
   *  mark saying so. */
  stroem?: FlowHour | null
}) {
  const t = useT()
  const el = useRef<HTMLDivElement>(null)
  const map = useRef<any>(null)
  const stroemLerret = useRef<HTMLCanvasElement>(null)
  /* Read once per frame by the flow field rather than closed over, so the hour
     can change under a running loop. A field still running last hour's bearing
     is worse than no field. */
  const stroemNaa = useRef<FlowHour | null | undefined>(stroem)
  stroemNaa.current = stroem
  const lib = useRef<any>(null)
  const marks = useRef<Map<string, any>>(new Map())
  const lerret = useRef<HTMLCanvasElement>(null)
  const [ready, setReady] = useState(0)
  const [layout, setLayout] = useState<SiteLayout | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let dead = false
    void siteLayout(siteId)
      .then((r) => { if (!dead) setLayout(r) })
      .catch((e) => {
        // A farm whose layout we cannot read is a farm with no picture, not a
        // page with an error box on it. The table below is the whole product
        // without this, and it is still there.
        if (!dead && !(e instanceof ApiError)) setFailed(true)
        if (!dead && e instanceof ApiError) setLayout(null)
      })
    return () => { dead = true }
  }, [siteId])

  const byPen = new Map(counts.map((c) => [c.pen_id, c]))
  const drawn = (layout?.pens ?? []).filter(
    (p) => p.lat != null && p.lon != null)

  /* All or nothing. One counted ring means the layer exists, and then a blank
     ring is a gap somebody has to explain rather than a missing layer. */
  const noneCounted = !counts.some((c) => c.pen_count_id !== null)
  const felles = noneCounted && siteValue !== null ? siteValue : null

  /* Whether this farm has said which fish stand where. Once it has, that is
     the better answer and it takes over; until then nothing changes for
     anybody. */
  const harFisk = (occupancy ?? []).length > 0

  /** What to colour this ring by.
   *
   *  Three answers, and the third is the one that was missing: a ring with no
   *  fish in it is EMPTY, and empty is not the same as uncounted. */
  const verdiFor = (penId: string): {
    v: number | null; avledet: boolean; tom: boolean; iMerd: string | null
  } => {
    if (harFisk) {
      const occ = byPenOcc.get(penId)
      // No fish here. Empty, and empty is a colour of its own.
      if (!occ) return { v: null, avledet: false, tom: true, iMerd: null }
      if (occ.lice) {
        return {
          v: occ.lice.adult_female, avledet: false, tom: false,
          // Where the figure was taken, when that is somewhere else. The fish
          // moved; the count did not stop having been taken where it was.
          iMerd: occ.lice.counted_in_pen
            !== (drawn.find((p) => p.pen_id === penId)?.pen_no ?? '')
            ? occ.lice.counted_in_pen : null,
        }
      }
      /* FISH WITH NO COUNT OF THEIR OWN YET, which is every farm on the day it
         first says what is in the water — and every count taken before
         `occupancy_id` existed, which cannot be attributed to a population
         afterwards without guessing.
         Falling through to what we do know, rather than blanking six rings
         that were showing a figure a minute ago. The ring's own count first,
         then the farm's — both already carry their own honest marking. */
      const eget = byPen.get(penId)?.adult_female ?? null
      if (eget !== null) {
        return { v: eget, avledet: false, tom: false, iMerd: null }
      }
      if (siteValue !== null) {
        return { v: siteValue, avledet: true, tom: false, iMerd: null }
      }
      return { v: null, avledet: false, tom: false, iMerd: null }
    }
    if (felles !== null) {
      return { v: felles, avledet: true, tom: false, iMerd: null }
    }
    return { v: byPen.get(penId)?.adult_female ?? null, avledet: false,
             tom: false, iMerd: null }
  }

  const byPenOcc = new Map((occupancy ?? []).map((o) => [o.pen_id, o]))

  const now = useRef({ selected, drawn, byPenOcc, onMoveFish, onDropTarget,
                       onArmFish })
  now.current = { selected, drawn, byPenOcc, onMoveFish, onDropTarget,
                  onArmFish }

  /* ------------------------------------------------------------- the map */

  useEffect(() => {
    if (!TOKEN || drawn.length === 0 || map.current) return
    let dead = false
    let m: any
    void (async () => {
      try {
        const maplibregl = (await import('maplibre-gl')).default
        await import('maplibre-gl/dist/maplibre-gl.css')
        if (dead || !el.current) return
        lib.current = maplibregl
        m = new maplibregl.Map({
          container: el.current,
          attributionControl: false,
          // The reader is looking at their own farm, not navigating. Pitch and
          // rotation buy nothing here and both make a ring's position harder to
          // compare against the one beside it.
          dragRotate: false,
          pitchWithRotate: false,
          style: {
            version: 8,
            sources: {
              satellite: {
                type: 'raster',
                tiles: [`https://api.tiles.mapbox.com/v4/mapbox.satellite/{z}/{x}/{y}.jpg?access_token=${TOKEN}`],
                tileSize: 256,
                attribution: '© Mapbox · © OpenStreetMap',
              },
            },
            layers: [{ id: 'satellite', type: 'raster', source: 'satellite' }],
          },
          center: [drawn[0]!.lon as number, drawn[0]!.lat as number],
          zoom: 15,
        })
        map.current = m
        m.touchZoomRotate.disableRotation()
        m.addControl(new maplibregl.NavigationControl({ showCompass: false }),
                     'top-right')
        m.addControl(new maplibregl.AttributionControl({ compact: true }),
                     'bottom-right')
        m.on('load', () => {
          m.addSource('rings',
                      { type: 'geojson',
                        data: { type: 'FeatureCollection', features: [] } })
          const colour = ['case',
            ['==', ['get', 'kind'], 'structure'], C_STRUCTURE,
            ['match', ['to-number', ['get', 'band']],
              1, BANDS[1]!, 2, BANDS[2]!, 3, BANDS[3]!, C_NONE]] as any
          m.addLayer({
            id: 'rings-fill', type: 'fill', source: 'rings',
            paint: {
              'fill-color': colour,
              /* THE ARRAY MELTS BETWEEN WEEKS RATHER THAN SNAPPING.
                 The playhead under the map scrubs the whole array through the
                 season, and without this every ring changes band in one frame
                 -- seven hard cuts, which reads as a slideshow of separate
                 states rather than as one farm changing. Six hundred
                 milliseconds is slow enough to see a ring go amber and fast
                 enough that dragging the playhead still feels attached to the
                 finger. It costs nothing at rest: no colour is changing, so
                 no frame is drawn. */
              'fill-color-transition': { duration: 600 },
              /* THE ARRAY ARRIVES RATHER THAN BEING ALREADY THERE, and this is
                 the one animation on this screen that is purely about the
                 first three seconds.
                 Principle 9 permits it and names the test: "it makes the
                 product feel calm, serious, trustworthy — that is also
                 function, because these people are deciding whether to trust a
                 number." A satellite tile and eight polygons drawn in the same
                 frame read as a screenshot. Drawn a beat apart, the polygons
                 read as OURS, laid over somebody else's picture, which is
                 exactly what they are. It happens once per mount and never
                 again while anybody is working. */
              'fill-opacity': 0,
              'fill-opacity-transition': { duration: 800, delay: 120 },
            },
          })
          /* ONE LINE LAYER, AND IT IS SOLID.
             There were two, because `line-dasharray` cannot be data-driven,
             and the dashed one meant "this figure is the farm's, not this
             ring's". The dashes are gone: a ring is a real object with a real
             edge, and dashing that edge to annotate the NUMBER inside it asks
             the reader to decode a stroke style. The caption on the picture
             says it in a sentence instead, which is the version somebody
             actually reads. */
          m.addLayer({
            id: 'rings-line', type: 'line', source: 'rings',
            paint: {
              'line-color': colour,
              'line-color-transition': { duration: 600 },
              'line-width': ['case', ['to-boolean', ['get', 'selected']],
                             3.5, 2],
              /* And the edge thickens into the selection instead of jumping to
                 it. Quicker than the colour: this one answers a tap, and an
                 answer to a tap that takes six hundred milliseconds reads as
                 lag rather than as grace. */
              'line-width-transition': { duration: 180 },
              'line-opacity': 0,
              'line-opacity-transition': { duration: 800, delay: 120 },
            },
          })
          /* THE CURRENT, ABOVE THE RINGS AND BELOW THE NUMBERS.
             A fill layer and not a line, because MapLibre lines have no
             arrowhead: the arrow is a closed polygon built in metres and
             rotated to the bearing, so it scales with the ring the way the
             ring itself does. A `symbol` layer with `icon-rotate` was the
             other candidate and draws a fixed-pixel glyph, which at the zoom
             where a ring is forty pixels across stops being beside the cage
             and starts being on top of it. */
          m.addSource('stroem',
                      { type: 'geojson',
                        data: { type: 'FeatureCollection', features: [] } })
          /* TWO LAYERS, ONE SOURCE, AND EACH OPACITY IS A CONSTANT.
             This was one layer with a data-driven `fill-opacity` AND an
             opacity transition on it. MapLibre does not support a transition
             on a data-driven paint property, and the combination took the GL
             renderer down a few seconds in -- the map went white while the
             page around it kept working. A filter per side costs one more
             layer and makes both opacities plain numbers, which is the shape
             transitions are actually defined for.

             NEW WATER READS LOUDER THAN USED WATER: the trail arriving at the
             cage is what the reader is being asked about, the one leaving is
             context for it. Same colour, so it is plainly the same current,
             and a step of opacity rather than a second hue. Both under the
             rings' own 0.42 -- the forecast is context for the counts, not a
             rival to them. */
          const stroemFarge = ['match', ['get', 'band'],
            'slack', STROEM_BAND.slack!, 'weak', STROEM_BAND.weak!,
            'moderate', STROEM_BAND.moderate!, 'good', STROEM_BAND.good!,
            'strong', STROEM_BAND.strong!, C_STROEM] as any
          for (const [id, side, alpha] of
               [['stroem-inn', 'upstream', 0.92],
                ['stroem-ut', 'downstream', 0.40]] as const) {
            m.addLayer({
              id, type: 'fill', source: 'stroem',
              /* The slack mark carries side 'upstream', so it lands in the
                 brighter of the two and is drawn exactly once. */
              filter: ['==', ['get', 'side'], side] as any,
              paint: {
                'fill-color': stroemFarge,
                'fill-color-transition': { duration: 600 },
                'fill-opacity': alpha,
                'fill-opacity-transition': { duration: 600 },
              },
            })
          }
          setReady((n) => n + 1)
        })
        m.on('click', () => onSelect(null))
        m.on('error', (e: any) => {
          if (e?.error?.status === 401 || e?.error?.status === 403)
            setFailed(true)
        })
      } catch {
        if (!dead) setFailed(true)
      }
    })()
    return () => {
      dead = true
      marks.current.clear()
      try { m?.remove() } catch { /* already gone */ }
    }
  }, [drawn.length > 0])

  /* Frame the array once, then leave the camera alone. Re-fitting on every
     count would snap the view back while somebody is reading a corner of it. */
  const framed = useRef(false)
  /** Whether the polygons have been faded up. Once, per mount. */
  const tent = useRef(false)
  /** The move already played. A re-render must not swim the same fish twice. */
  const sett = useRef<string | null>(null)
  /** Whether the fish layer has ever been drawn. A badge that appears in the
   *  FIRST paint of that layer is the page loading, not fish arriving — and
   *  swimming it would replay whatever the last move happened to be every
   *  time somebody opens the farm. */
  const tegnet = useRef(false)
  useEffect(() => {
    const m = map.current, maplibregl = lib.current
    if (!m || !maplibregl || framed.current || drawn.length === 0 || !ready)
      return
    framed.current = true
    const pts = [
      ...drawn.map((p) => [p.lon as number, p.lat as number]),
      ...(layout?.structures ?? []).map((s) => [s.lon, s.lat]),
    ] as [number, number][]
    const b = pts.reduce((acc, p) => acc.extend(p),
                         new maplibregl.LngLatBounds(pts[0], pts[0]))
    m.fitBounds(b, { padding: 70, maxZoom: 17.5, duration: 0 })
  }, [ready, drawn.length])

  /* ------------------------------------------------ the rings themselves */

  useEffect(() => {
    const m = map.current, maplibregl = lib.current
    const src = m?.getSource?.('rings')
    if (!m || !maplibregl || !src) return

    const features = [
      ...(layout?.structures ?? []).map((s) => structureFeature(s)),
      ...drawn.map((p) => {
        const { v, avledet, tom } = verdiFor(p.pen_id)
        return penFeature(p, {
          band: v !== null ? (antallFor(v, grense) ?? 0) : 0,
          counted: v !== null,
          avledet, tom,
          selected: selected === p.pen_id,
        })
      }),
    ].filter(Boolean)
    src.setData({ type: 'FeatureCollection', features })

    /* Now the layers may be seen. Set after the first setData rather than at
       addLayer, because a transition needs a value to travel FROM and the
       layer was added at zero. Idempotent: setting the same expression again
       is a no-op that costs nothing on later renders. */
    if (!tent.current) {
      tent.current = true
      m.setPaintProperty('rings-fill', 'fill-opacity', ['case',
        // A counted ring is filled; an uncounted one is nearly hollow. The
        // difference has to survive being glanced at from a metre away on a
        // phone in daylight, which colour alone does not.
        ['to-boolean', ['get', 'tom']], 0.03,
        ['to-boolean', ['get', 'counted']], 0.42, 0.08] as any)
      for (const id of ['rings-line']) {
        m.setPaintProperty(id, 'line-opacity', 1)
      }
    }

    // The number painted on the ring, and what was found in it. Two lines in
    // one mark rather than two marks: they are one fact about one ring, and
    // splitting them means the eye pairs them up eight times.
    const want = new Set<string>()
    for (const [i, p] of drawn.entries()) {
      want.add(p.pen_id)
      const c = byPen.get(p.pen_id)
      const { v, avledet, tom, iMerd } = verdiFor(p.pen_id)
      const band = v !== null ? antallFor(v, grense) : null
      let mk = marks.current.get(p.pen_id)
      if (!mk) {
        const node = document.createElement('button')
        node.type = 'button'
        node.classList.add('merdvis')
        node.append(document.createElement('b'), document.createElement('i'))
        node.addEventListener('click', (e) => {
          e.stopPropagation()
          onSelect(now.current.selected === p.pen_id ? null : p.pen_id)
        })
        mk = new maplibregl.Marker({ element: node })
        // POSITION BEFORE addTo, and the order is not a style choice.
        // addTo() projects the marker immediately, and a marker with no lngLat
        // makes LngLat.convert(undefined) throw inside an effect -- which React
        // rethrows, unmounting the whole tree. The page rendered, then went
        // blank.
        mk.setLngLat(nordvest(p.lat as number, p.lon as number,
                              radiusM(p.circumference_m))).addTo(m)
        /* Walking order, so the array introduces itself along its own line
           rather than all at once. Read once by CSS as an animation delay; it
           is a position in a sequence and nothing else depends on it. */
        node.style.setProperty('--i', String(i))
        marks.current.set(p.pen_id, mk)
      }
      mk.setLngLat(nordvest(p.lat as number, p.lon as number,
                            radiusM(p.circumference_m)))
      const node = mk.getElement() as HTMLElement
      node.querySelector('b')!.textContent = p.pen_no
      /* THE FARM'S OWN FIGURE IS NOT PRINTED SEVEN TIMES.
         When no ring has been counted, every ring is showing one number, and
         seven copies of 0,40 is the same fact said seven times — the reader
         has to check all of them before they can be sure it is not seven
         different measurements that happen to agree. The colour carries it
         across the array and the caption under the map says it once, in
         words, with its provenance. What is left on the ring is its number,
         which is the thing that differs. */
      node.querySelector('i')!.textContent = tom ? t('empty')
        : avledet ? ''
          : v !== null ? nb(v)
            : (c?.not_counted_reason ? '–' : '')
      for (const n of [0, 1, 2, 3]) {
        node.classList.toggle(`b${n}`, (band ?? 0) === n)
      }
      node.classList.toggle('on', selected === p.pen_id)
      node.classList.toggle('utelt', v === null && !tom)
      node.classList.toggle('tom', tom)
      node.classList.toggle('avledet', avledet)
      node.title = tom ? t('No fish in this pen.')
        : iMerd ? `${t('Counted in pen')} ${iMerd}`
          : avledet ? t("The farm's figure, not this pen's.")
            : c?.not_counted_reason ? t(`skipped:${c.not_counted_reason}`) : ''
    }
    for (const [key, mk] of marks.current) {
      if (!want.has(key) && !key.startsWith('fisk:')) {
        mk.remove()
        marks.current.delete(key)
      }
    }
  }, [layout, counts, siteValue, grense, selected, ready])

  /* THE FLOW FIELD. Water moving through the rings at the forecast's own
     speed, on a canvas over the map.

     Motion is the one channel an arrow does not have: an arrow must draw speed
     as length, and length reads as distance. Here the speed IS the speed, on a
     fixed time-lapse gain — so two hours that differ look different, and the
     ratio between them is right.

     Under prefers-reduced-motion `startStroemning` returns immediately and the
     chevrons in `stroem-inn`/`stroem-ut` carry it alone. */
  useEffect(() => {
    const m = map.current, flate = stroemLerret.current
    if (!m || !flate || !ready) return

    const stopp = startStroemning(flate, m, () => {
      const h = stroemNaa.current
      if (!h?.current) return []
      const merker = new Map(h.pens.map((k) => [k.pen_no, k]))
      const ut: Stroemning[] = []
      for (const p of drawn) {
        const k = merker.get(p.pen_no)
        if (!k) continue
        ut.push({
          penId: p.pen_id,
          lon: p.lon as number, lat: p.lat as number,
          radiusM: radiusM(p.circumference_m),
          bearing: k.downstream_bearing,
          speed: h.current.speed_ms,
          band: k.exchange ?? 'moderate',
          slack: k.slack,
        })
      }
      return ut
    })

    /* IF THE GL CONTEXT GOES, THIS LAYER GETS OUT OF THE WAY.
       Losing the context is what turned the map white, and whatever caused it,
       a canvas animation competing for the same GPU is the last thing that
       should still be running while the browser tries to give it back. The map
       is the product; the current is a layer on it. */
    const tapt = () => stopp()
    const gl = m.getCanvas?.()
    gl?.addEventListener('webglcontextlost', tapt)

    return () => {
      gl?.removeEventListener('webglcontextlost', tapt)
      stopp()
    }
  }, [ready])

  /* ------------------------------------------------------------- the strøm */

  /* Two arrows per ring: the side the water arrives on and the side it leaves
     by, both pointing the way it goes. One arrow beside a cage is ambiguous
     about whether it describes that cage or the gap beside it; the pair reads
     as "through this one", which is the actual claim being made.

     Everything geographic. The arrow is built in metres from the ring's own
     radius and projected with the same offset()/turn() that drew the ring, so
     at every zoom it stays the same distance off the same rim. A pixel offset
     would sit on the rim at one zoom and adrift in the water at the next --
     the argument nordvest() above makes, and it applies harder here, because
     an arrow that has drifted off its cage is still pointing somewhere and
     still looks like it means it. */
  useEffect(() => {
    const m = map.current
    const src = m?.getSource?.('stroem')
    if (!m || !src) return

    const marks = new Map((stroem?.pens ?? []).map((k) => [k.pen_no, k]))
    const features: DrawnFeature[] = []

    /* No hour, or an hour the forecast has no current for. Draw nothing: a gap
       in a forecast is not slack water, and the panel says which this is in
       words rather than the map saying it in a shape. */
    if (stroem?.current) {
      for (const p of drawn) {
        const k = marks.get(p.pen_no)
        if (!k) continue

        if (k.slack || k.length_r === null) {
          features.push({
            type: 'Feature',
            properties: { kind: 'slack', pen_no: p.pen_no,
                          band: k.exchange ?? 'slack', side: 'upstream' },
            geometry: {
              type: 'Polygon',
              coordinates: [slackRing(p.lat as number, p.lon as number,
                                      p.circumference_m, k.standoff_r)],
            },
          })
          continue
        }

        const { upstream, downstream } = flowArrows(
          p.lat as number, p.lon as number, p.circumference_m,
          k.downstream_bearing, k.standoff_r, k.length_r,
          chevronCount(k.exchange))

        for (const [side, trail] of
             [['upstream', upstream], ['downstream', downstream]] as const) {
          for (const ring of trail) {
            features.push({
              type: 'Feature',
              properties: { kind: 'arrow', side, pen_no: p.pen_no,
                            band: k.exchange ?? 'moderate' },
              geometry: { type: 'Polygon', coordinates: [ring] },
            })
          }
        }
      }
    }

    src.setData({ type: 'FeatureCollection', features })
  }, [stroem, layout, ready])

  /* ------------------------------------------------------------ the badges */

  useEffect(() => {
    const m = map.current, maplibregl = lib.current
    if (!m || !maplibregl || !ready) return

    const førstePaint = !tegnet.current
    if (byPenOcc.size > 0) tegnet.current = true

    const want = new Set<string>()
    for (const [i, p] of drawn.entries()) {
      const occ = byPenOcc.get(p.pen_id)
      if (!occ) continue
      const key = `fisk:${p.pen_id}`
      want.add(key)
      let mk = marks.current.get(key)
      let nyfødt = false
      if (!mk) {
        const node = document.createElement('button')
        node.type = 'button'
        node.classList.add('fiskemerke')
        node.append(fishSvg(), document.createElement('span'))
        // TAP TO ARM, and it is the path that has to work. See onArmFish.
        node.addEventListener('click', (e) => {
          e.stopPropagation()
          now.current.onArmFish?.(p.pen_id)
        })
        // Draggable only when a move can actually be recorded. A handle that
        // moves under the finger and then answers 403 teaches somebody that
        // the product is broken at the moment they first trust it.
        /* DEAD CENTRE OF THE RING, AND NOTHING AROUND IT.
           It used to be a chip -- dark pill, border, shadow, the group's name
           beside a fish -- hanging under the ring's number, which made every
           merd carry two boxes stacked in the same few pixels. The centre was
           occupied by the number and is now free, and the centre is where the
           fish are: a silhouette in the middle of the ring needs no label to
           say what it is or which ring it belongs to. The name went with the
           box; it is on the marker's title and in the panel, where a word is
           readable rather than three letters at nine pixels over open water. */
        mk = new maplibregl.Marker({
          element: node, draggable: !!onMoveFish,
        })
        node.style.setProperty('--i', String(i))
        mk.setLngLat([p.lon as number, p.lat as number]).addTo(m)

        if (onMoveFish) {
          /* WHERE THE FINGER ACTUALLY IS, tracked through the drag.
             MapLibre's dragend gives a lngLat and nothing else, which cannot
             say "you let go over the slaughterhouse" — that target is ordinary
             HTML outside the canvas. So the pointer is followed and the drop is
             resolved against the DOM first and the map second. */
          let last: { x: number; y: number } | null = null
          const track = (e: PointerEvent | TouchEvent) => {
            const t = 'touches' in e ? e.changedTouches[0] : e
            if (t) last = { x: (t as any).clientX, y: (t as any).clientY }
          }
          mk.on('dragstart', () => {
            last = null
            window.addEventListener('pointermove', track as any, true)
            window.addEventListener('touchmove', track as any, true)
          })
          mk.on('dragend', () => {
            window.removeEventListener('pointermove', track as any, true)
            window.removeEventListener('touchmove', track as any, true)
            const at = mk.getLngLat()
            // Put it back FIRST, always. The badge's position is decided by
            // where the fish are, and the fish do not move until the receiver
            // says so -- so the marker returns home and the sheet decides.
            mk.setLngLat([p.lon as number, p.lat as number])

            // A tray target — slaughterhouse, delousing — before anything
            // else. It is ordinary HTML and the map knows nothing about it.
            if (last) {
              const el = document.elementFromPoint(last.x, last.y)
              const tray = el?.closest?.('[data-fishdrop]') as HTMLElement | null
              if (tray?.dataset.fishdrop) {
                now.current.onDropTarget?.(p.pen_id, tray.dataset.fishdrop)
                return
              }
            }

            /* No correction any more. The badge used to hang below its
               anchor, so a drop landed twenty metres north of where the
               reader had aimed and could resolve to nothing; now the badge IS
               its anchor and where you let go is where you let go. */
            const hit = penAt(now.current.drawn,
                              { lat: at.lat, lon: at.lng })
            if (!hit || hit.pen_id === p.pen_id) return
            now.current.onMoveFish?.(p.pen_id, hit.pen_id)
          })
        }
        marks.current.set(key, mk)
        nyfødt = true
      }
      mk.setLngLat([p.lon as number, p.lat as number])

      /* THE FISH SWIM THERE.
         A badge born in the ring the fish just arrived in is correct and
         unreadable: one vanishes, another appears, and during a shuffle of
         four moves the reader has no way to tell which went where except by
         re-reading the whole array. So a badge created by a move THAT WAS
         JUST WRITTEN starts at the ring the fish left and travels — half a
         second, and the answer to "did that go where I meant" is the gesture
         playing itself back.

         Only for the newest move, only once, and only when both ends are
         drawn. Everything else appears where it belongs. */
      const flytt = sisteFlytt
      if (nyfødt && !førstePaint && flytt && flytt.pen_id === p.pen_id
          && flytt.from_pen_id && sett.current !== flytt.occupancy_id) {
        sett.current = flytt.occupancy_id
        const fra = drawn.find((d) => d.pen_id === flytt.from_pen_id)
        if (fra?.lat != null && fra.lon != null) {
          svøm(mk, [fra.lon, fra.lat], [p.lon as number, p.lat as number])
          // The ring it landed in says so once, on its own mark, so the eye
          // that followed the badge has somewhere to stop.
          const ringen = marks.current.get(p.pen_id)
          const rel = ringen?.getElement() as HTMLElement | undefined
          if (rel) {
            rel.classList.remove('traff')
            // Reflow, or the class never left as far as the animation is
            // concerned and a second move into the same ring plays nothing.
            void rel.offsetWidth
            rel.classList.add('traff')
            window.setTimeout(() => rel.classList.remove('traff'), 900)
          }
        }
      }

      const node = mk.getElement() as HTMLElement
      // The name only where it distinguishes something. One generation across
      // seven rings paints seven identical tags, which is decoration by the
      // design system's own definition -- "an icon beside a label that already
      // says the word". The fish says there are fish here; the letters only
      // earn their place once there are two populations to tell apart.
      /* The name is not painted on the water. Even where two populations
         share an array -- which is the only case it ever earned -- three
         letters at nine pixels over a satellite tile is a label nobody reads
         and a box somebody has to draw to make it legible. It is on the
         title and in the panel. */
      node.classList.toggle('navn', false)
      node.classList.toggle('armert', armedFrom === p.pen_id)
      node.title = `${occ.group_name}${occ.n_fish
        ? ` · ${occ.n_fish.toLocaleString('nb-NO')} ${t('fish')}` : ''}`
    }
    for (const [key, mk] of marks.current) {
      if (key.startsWith('fisk:') && !want.has(key)) {
        mk.remove()
        marks.current.delete(key)
      }
    }
  }, [layout, occupancy, ready, !!onMoveFish, armedFrom, showNames,
      sisteFlytt?.occupancy_id])

  /* ---------------------------------------------------------------- fisken
     som faktisk står i ringen. Se sverm.ts for hvorfor dette er en stim og
     ikke et strømfelt: vi har ingen hydrodynamikk, og partikler som driver
     over en ekte fjord ville vært en animasjon av en strøm ingen har målt. */

  /* Read on every frame instead of closed over. The rings are recoloured by
     props on every new count, and a school still swimming last week's colours
     is worse than no school at all. */
  const svermNå = useRef<Sverm[]>([])
  svermNå.current = drawn.map((p) => {
    const { v, tom } = verdiFor(p.pen_id)
    return {
      penId: p.pen_id,
      lon: p.lon as number,
      lat: p.lat as number,
      radiusM: radiusM(p.circumference_m),
      band: v !== null ? (antallFor(v, grense) ?? 0) : 0,
      tom,
      // Fish, but no figure to colour them by. Neutral, like the dashed ring.
      utelt: !tom && v === null,
    }
  })

  useEffect(() => {
    const m = map.current
    const c = lerret.current
    if (!m || !c || !ready) return
    return startSverm(c, m, () => svermNå.current)
  }, [ready])

  /* ------------------------------------------------------------- render */

  // Nothing placed. Not an error and not an empty state to apologise for: a
  // farm whose rings nobody has drawn has a table, which is what every farm
  // had until this shipped.
  if (!layout || drawn.length === 0 || failed || !TOKEN) {
    if (setupHref && layout && layout.pens.length > 0) {
      return (
        <p className="faint" style={{ margin: 0, fontSize: 13 }}>
          {t('This farm is not drawn yet.')}{' '}
          <a href={setupHref}>{t('Set up the pens')}</a>
        </p>
      )
    }
    return null
  }

  const missing = (layout.pens.length - drawn.length)

  const spredt = felles !== null
    || (harFisk && drawn.some((p) => verdiFor(p.pen_id).avledet))

  return (
    /* THE LEGEND AND THE CAPTION MOVED ONTO THE PICTURE.
       They were two more rows of small text under a card that already had
       four, and a reader scanning down the page met the ladder before they
       met anything the ladder explains. On the map they are where the colours
       are, they cost no height, and the map gets the room they were taking. */
    <div className={`kartramme${fyller ? ' fyller' : ''}`}>
      {/* `data-baerer` while a fish is in hand, so every ring can say it is a
          destination. What you carry looks carried; what you can drop it on
          looks like somewhere to drop it — which answers "what is selected"
          more plainly than any outline could. */}
      <div className="anleggskart" ref={el}
        data-baerer={armedFrom ? '' : undefined} />

      {/* THE SCHOOL. A canvas rather than markers: seven rings of forty motes
          is 280 moving things, and 280 DOM nodes reprojected every frame is a
          different product. `pointer-events: none` throughout — the fish are
          a picture of what is in the ring, and everything you can actually
          press is the ring itself, its badge, or the fish marker. */}
      <canvas className="stroemlerret" ref={stroemLerret} aria-hidden="true" />
      <canvas className="merdsverm" ref={lerret} aria-hidden="true" />

      {/* NO KEY. A LEGEND IS THE ADMISSION THAT COLOUR IS THE ONLY CARRIER.
          Every ring on this map prints its own figure inside itself, and the
          band underneath prints the limit. So the colour is a second reading
          of a number that is already there — redundancy, not the message —
          and a four-item key teaching a ladder nobody has to decode made the
          product look like it needed a manual. What the ladder does, it does
          without being announced: green farm, amber farm, red farm, at a
          glance and from a walkway.

          The one thing colour alone was saying is the dashed ring, and a
          dashed ring with no number in it already reads as nobody looked. */}

      {/* Said out loud, on the picture, whenever the picture is showing one
          number eight times. A dashed outline is a hint; this is the sentence.
          A farm at 0,45 paints eight amber rings, and the reader has to know
          that none of them is a measurement of that ring. */}
      {(spredt || missing > 0) && (
        <div className="kartnote">
          {spredt && (
            <p>
              {t('No count per pen for this day. Every pen is showing the farm figure,')}{' '}
              <strong>{nb(felles ?? siteValue ?? 0)}</strong>.
            </p>
          )}
          {missing > 0 && (
            <p>{missing} {t('pens are not on the map yet.')}</p>
          )}
          {/* WHOSE NUMBER THE ARROWS ARE. On the picture and not in a panel
              beside it, because this is the only thing on this map that is a
              FORECAST — everything else was counted by somebody on the
              walkway. A reader who has spent the last minute looking at lice
              will read the arrows the same way unless it is said, and it has
              to be said where the arrows are. */}
          {stroem?.current && (
            <p className="kartnote-stroem">
              <span className={`kartnote-band er-${stroem.current.exchange}`}>
                {t(`exchange:${stroem.current.exchange}`)}
              </span>
              {' '}
              <strong>{kompass(stroem.current.bearing_toward)}</strong>
              {' '}
              {stroem.current.slack
                ? ''
                /* Tilde: modellert, ikke målt — the mark this product already
                   uses for a figure nobody read off an instrument. */
                : `~${stroem.current.speed_ms.toFixed(2)} m/s `}
              · {t('Modelled, not measured.')}
            </p>
          )}
        </div>
      )}

      {/* The big version. A farm's array is three hundred metres of diagonal
          and a card is not where you do a shuffle; this is the door to the
          screen where you can. */}
      {fullskjermHref && (
        <Link className="kartknapp" to={fullskjermHref}
          aria-label={t('Open the map full screen')}>
          {t('Full screen')}
        </Link>
      )}
    </div>
  )
}
